import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useSearchParams,
} from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { SettingsProvider } from '@/contexts/SettingsContext';
import { CurrencyProvider } from '@/contexts/CurrencyContext';
import { CategoriesProvider } from '@/contexts/CategoriesContext';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useRecurringTransactionProcessor } from '@/hooks/useRecurringTransactionProcessor';
import { Login } from '@/pages/Login';
import { Signup } from '@/pages/Signup';
import { ResetPassword } from '@/pages/ResetPassword';
import { Dashboard } from '@/pages/Dashboard';
import { MonthView } from '@/pages/MonthView';
import { Settings } from '@/pages/Settings';
import { RecurringTransactions } from '@/pages/RecurringTransactions';
import { Landing } from '@/pages/Landing';
import { PrivacyPolicy } from '@/pages/PrivacyPolicy';
import { TermsOfService } from '@/pages/TermsOfService';
import { TripDashboard } from '@/pages/TripDashboard';

/**
 * Runs the recurring transaction processor on every page load,
 * not just on the Dashboard. Must be inside AuthProvider + SettingsProvider.
 */
function RecurringProcessor() {
  useRecurringTransactionProcessor();
  return null;
}

/**
 * Handles the root "/" path.
 * Firebase password-reset links land here with ?mode=resetPassword&oobCode=...
 * Any other visit to "/" shows the Landing page.
 */
function RootHandler() {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');

  if (mode === 'resetPassword') {
    return <ResetPassword />;
  }

  return <Landing />;
}

function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AuthProvider>
          <SettingsProvider>
            <CurrencyProvider>
                <CategoriesProvider>
                <RecurringProcessor />
                <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />

                {/* Protected routes */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/month/:month"
                  element={
                    <ProtectedRoute>
                      <MonthView />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/recurring"
                  element={
                    <ProtectedRoute>
                      <RecurringTransactions />
                    </ProtectedRoute>
                  }
                />

                {/* Public shared trip dashboard */}
                <Route path="/trip/:tripId" element={<TripDashboard />} />

                {/* Root "/" — Landing page or password reset handler */}
                <Route path="/" element={<RootHandler />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
                </CategoriesProvider>
            </CurrencyProvider>
          </SettingsProvider>
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
